import { VERDICT_LABELS } from "../copy/reviewStatusCopy";

export function abbreviateSha(sha) {
  const value = String(sha || "");
  return value ? value.slice(0, 7) : "";
}

export function verdictLabel(verdict) {
  return VERDICT_LABELS[verdict] || "Review completed";
}

export function formatWhen(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function formatDuration(createdAt, completedAt) {
  if (!createdAt || !completedAt) return "";
  const ms = new Date(completedAt).getTime() - new Date(createdAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  if (ms < 1000) return "under a second";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

export function decisionSummary(review) {
  const count = (review?.findings || []).length;
  if (review?.verdict === "insufficient_context") {
    return "This pull request needs more product context before these observations can be trusted.";
  }
  if (review?.verdict === "discuss_first") {
    const concerns = Math.max(count - 1, 1);
    return `RenderRepo found ${concerns} concern${concerns === 1 ? "" : "s"} and one open question.`;
  }
  if (review?.verdict === "failed" || review?.status === "failed") {
    return "The review did not finish. The pull request itself was not marked wrong.";
  }
  if (count > 0) {
    return `This pull request appears to make ${count} product decision${count === 1 ? "" : "s"}.`;
  }
  return "RenderRepo did not find a product decision in this pull request.";
}

export function publicationView(review, capability) {
  const state = review?.publication?.state || review?.publicationState || null;
  const code = capability?.code || review?.publication?.errorCode || review?.publicationErrorCode || null;
  if (review?.currentState === "stale" && state !== "published") {
    return { id: "stale", label: "Review became stale before publication", tone: "stale" };
  }
  if (state === "published") return { id: "published", label: "Published to GitHub", tone: "good" };
  if (state === "pending") return { id: "pending", label: "Publication pending", tone: "progress" };
  if (code === "CHECK_PERMISSION_REQUIRED") return { id: "permission", label: "Checks permission required", tone: "setup" };
  if (code === "CHECK_INSTALLATION_SUSPENDED") return { id: "reauth", label: "Installation reauthorization required", tone: "setup" };
  if (code === "CHECK_INSTALLATION_REQUIRED" || code === "CHECK_REPOSITORY_NOT_AUTHORIZED") {
    return { id: "reauth", label: "Installation reauthorization required", tone: "setup" };
  }
  if (state === "disabled" || (capability && capability.flagEnabled === false && capability.checksWrite)) {
    return { id: "disabled", label: "Publication disabled", tone: "muted" };
  }
  if (state === "failed" || state === "skipped" || code) return { id: "failed", label: "Publication failed", tone: "setup" };
  return { id: "unknown", label: "Not published to GitHub", tone: "muted" };
}

function publicationNeedsAttention(item) {
  const view = publicationView(item);
  return view.id === "permission" || view.id === "reauth" || view.id === "failed";
}

/** Current completed Decision Reviews that need a human product decision. */
export function needsHumanDecision(review) {
  if (!review || review.type === "acceptance") return false;
  if (review.currency === "stale" || review.currentState === "stale" || review.status === "superseded") return false;
  if (review.status !== "completed" && review.activity !== "completed") return false;
  return review.verdict === "discuss_first" || review.verdict === "changes_recommended";
}

export function normalizeWorkspaceReviews(payload) {
  const decision = (payload?.decisionReviews || []).map((review) => {
    const activity = review.status === "failed" ? "failed" : review.status === "analyzing" || review.status === "queued" ? "running" : "completed";
    const currency = review.currentState === "stale" || review.status === "superseded" ? "stale" : "current";
    const item = {
      ...review,
      type: "decision",
      activity,
      currency,
      countLabel: `${review.findingCount || 0} findings`,
    };
    item.needsDecision = needsHumanDecision(item);
    item.needsAttention =
      currency !== "stale" &&
      (item.needsDecision ||
        review.status === "failed" ||
        review.status === "analyzing" ||
        review.status === "queued" ||
        publicationNeedsAttention(review));
    return item;
  });
  const acceptance = (payload?.acceptanceReviews || []).map((review) => ({
    ...review,
    type: "acceptance",
    activity: review.status === "failed" ? "failed" : review.status === "pending" ? "running" : "completed",
    currency: review.currentState === "stale" ? "stale" : "current",
    countLabel: `${review.criteriaCount || 0} criteria`,
    needsDecision: false,
    needsAttention: review.currentState !== "stale" && (review.status !== "completed" || review.verdict === "not_evaluated"),
  }));
  return [...decision, ...acceptance];
}

export function matchesReviewFilter(item, filter, type) {
  if (type && type !== "all" && item.type !== type) return false;
  if (filter === "all") return true;
  if (filter === "running") return item.activity === "running";
  if (filter === "failed") return item.activity === "failed";
  if (filter === "historical") return item.currency === "stale";
  if (filter === "completed") return item.activity === "completed" && item.currency !== "stale";
  // Overview "needs a decision" and this filter must stay identical.
  if (filter === "attention") return needsHumanDecision(item);
  if (filter === "current" || filter === "needs-attention") return item.currency !== "stale";
  return true;
}

export function reviewNextAction(item) {
  if (item.currency === "stale") return "View historical review";
  if (item.activity === "running") return "Follow progress";
  if (item.activity === "failed") return "See what happened";
  if (item.type === "acceptance") return "Open review";
  return "Open review";
}
